/* eslint-disable @typescript-eslint/consistent-type-definitions -- we need to use `interface` in order to override MUIs interfaces*/
import '@hg-akademie-ban/ban-ui-lib/fonts.css';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

import { createBanElevateTheme } from '@hg-akademie-ban/ban-ui-lib';
import { CheckCircleOutline, DoNotDisturb, ErrorOutline } from '@mui/icons-material';
import { alpha, darken, lighten } from '@mui/material';
import { createElement } from 'react';

import { colorPalette, elevateColorPalette } from './colorPalette';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    headingXxl: React.CSSProperties;
    headingXl: React.CSSProperties;
    headingLg: React.CSSProperties;
    headingMd: React.CSSProperties;
    headingSm: React.CSSProperties;
    headingXs: React.CSSProperties;
    headingXxs: React.CSSProperties;
    paragraphXl: React.CSSProperties;
    paragraphLg: React.CSSProperties;
    paragraph: React.CSSProperties;
    paragraphSm: React.CSSProperties;
    labelLg: React.CSSProperties;
    labelLgLight: React.CSSProperties;
    labelLgMultiline: React.CSSProperties;
    labelLgMultilineLight: React.CSSProperties;
    label: React.CSSProperties;
    labelLight: React.CSSProperties;
    labelMultiline: React.CSSProperties;
    labelMultilineLight: React.CSSProperties;
    labelSm: React.CSSProperties;
    labelSmLight: React.CSSProperties;
  }

  // allow configuration using `createTheme()`
  interface TypographyVariantsOptions {
    headingXxl?: React.CSSProperties;
    headingXl?: React.CSSProperties;
    headingLg?: React.CSSProperties;
    headingMd?: React.CSSProperties;
    headingSm?: React.CSSProperties;
    headingXs?: React.CSSProperties;
    headingXxs?: React.CSSProperties;
    paragraphXl?: React.CSSProperties;
    paragraphLg?: React.CSSProperties;
    paragraph?: React.CSSProperties;
    paragraphSm?: React.CSSProperties;
    labelLg?: React.CSSProperties;
    labelLgLight?: React.CSSProperties;
    labelLgMultiline?: React.CSSProperties;
    labelLgMultilineLight?: React.CSSProperties;
    label?: React.CSSProperties;
    labelLight?: React.CSSProperties;
    labelMultiline?: React.CSSProperties;
    labelMultilineLight?: React.CSSProperties;
    labelSm?: React.CSSProperties;
    labelSmLight?: React.CSSProperties;
  }
}

// TODO ban-ui-lib?: some of the values here can be implemented in ban-ui-lib and then be removed here
const baseTheme = createBanElevateTheme({
  themeOverrides: {
    spacing: 8,
    palette: colorPalette,
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            // TODO ban-ui-lib: This is to prevent `00:00` to look like `00 : 00`. see below
            fontVariantLigatures: 'no-contextual',
          },
          columnHeader: {
            backgroundColor: '#190333',
            color: '#FFFFFF',
            '& svg': { color: 'white !important' },
          },
          sortIcon: {
            color: '#FFFFFF',
          },
          menuIcon: {
            '& svg': {
              color: '#FFFFFF',
            },
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variant: 'paragraph',
          color: 'textPrimary',
        },
        styleOverrides: {
          root: {
            // TODO ban-ui-lib: This is to prevent `00:00` to look like `00 : 00`. Either this should be fixed in the font by Brand
            //       or we put this into the ban-ui-lib for paragraph, paragraphSm, paragraphXl (these are the only variants where it happens)
            fontVariantLigatures: 'no-contextual',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              boxShadow: 'none',
            },
          },
        },
        variants: [
          {
            props: { variant: 'menu' },
            style: {
              borderRadius: '8px',
              '&:hover': {
                color: elevateColorPalette.text.brand,
                backgroundColor: elevateColorPalette.surface.brandLight,
              },
              justifyContent: 'flex-start',
              padding: '10px 12px',
              fontSize: '14px',
            },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: elevateColorPalette.text.primary,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            border: `1px solid ${elevateColorPalette.border.subtle}`,
            borderRadius: 16,
            color: elevateColorPalette.text.primary,
          },
        },
        variants: [
          {
            props: { variant: 'tag' },
            style: {
              borderRadius: 4,
              border: 'none',
              color: elevateColorPalette.text.primaryOnBrand,
            },
          },
          {
            props: { variant: 'selection' },
            style: {
              '&:hover': {
                backgroundColor: elevateColorPalette.surface.brandLightHover,
              },
            },
          },
        ],
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            border: `1px solid ${elevateColorPalette.border.emphasised}`,
            borderRadius: '8px !important',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: `1px solid ${elevateColorPalette.border.default}`,
            backgroundColor: elevateColorPalette.surface.background,
            borderRadius: '8px',
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          content: {
            fontFamily: 'TWK Haufe Akademie Everett, Arial',
          },
          expandIconWrapper: {
            color: elevateColorPalette.text.brand,
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            border: `1px solid ${elevateColorPalette.border.emphasised}`,
            borderRadius: '36px',
            padding: 2,
            height: '36px',
          },
          grouped: {
            margin: 0,
            border: 'none',
            borderRadius: '30px',
            '&:not(:first-of-type)': { marginLeft: 4 },
            '&.Mui-disabled': { border: 'none' },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            color: elevateColorPalette.text.primary,
            border: 'none',
            height: '30px',
            padding: '0 12px',
            '&.Mui-selected': {
              backgroundColor: elevateColorPalette.surface.brand,
              color: elevateColorPalette.text.primaryOnBrand,
              '&:hover': {
                backgroundColor: elevateColorPalette.surface.brandHover,
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 24,
            padding: 0,
            marginRight: 12,
          },
          switchBase: {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(18px)',
              color: elevateColorPalette.surface.recessed,
              '& + .MuiSwitch-track': {
                backgroundColor: lighten(elevateColorPalette.surface.brand, 0.1),
                border: 'none',
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: elevateColorPalette.surface.background,
              },
              '&:hover:not(.Mui-disabled)': {
                '& .MuiSwitch-thumb': {
                  backgroundColor: elevateColorPalette.surface.background,
                },
                '& + .MuiSwitch-track': {
                  backgroundColor: elevateColorPalette.surface.brandHover,
                },
              },
              '&.Mui-disabled': {
                '& .MuiSwitch-thumb': {
                  backgroundColor: elevateColorPalette.surface.background,
                },
                '& + .MuiSwitch-track': {
                  backgroundColor: elevateColorPalette.surface.brandLightHover,
                },
              },
            },
            '&:hover:not(.Mui-disabled)': {
              '& + .MuiSwitch-track': {
                backgroundColor: elevateColorPalette.text.secondary,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: elevateColorPalette.surface.background,
              },
            },
            '&.Mui-disabled': {
              '& .MuiSwitch-thumb': {
                backgroundColor: elevateColorPalette.surface.background,
              },
              '& + .MuiSwitch-track': {
                borderColor: elevateColorPalette.surface.disabledSolid,
              },
            },
          },
          thumb: {
            width: 20,
            height: 20,
            boxShadow: 'none',
            backgroundColor: elevateColorPalette.surface.background,
            transition: 'background-color 0.2s ease',
          },
          track: {
            borderRadius: 26,
            backgroundColor: elevateColorPalette.text.disabledSolid,
            opacity: 1,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            padding: '8px 16px',
            color: elevateColorPalette.text.secondary,
            backgroundColor: `${elevateColorPalette.surface.background} !important`,
          },
          icon: {
            '.MuiAlert-standardInfo &': {
              color: `${elevateColorPalette.text.primary} !important`,
            },
          },
        },
        defaultProps: {
          iconMapping: {
            success: createElement(CheckCircleOutline),
            warning: createElement(ErrorOutline),
            error: createElement(DoNotDisturb),
          },
        },
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.125rem !important', // typography variant `headingXxs`
            lineHeight: '1.4 !important', // for better alignment with the icon
            marginBottom: '10px',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: elevateColorPalette.surface.brandDark,
            color: elevateColorPalette.text.primaryOnBrand,
            maxWidth: '280px',
            borderRadius: 8,
            boxShadow: '0px 3px 5px 2px rgba(0,0,0,0.3)',
            padding: '12px',
            fontSize: '0.75rem', // typography variant `paragraphSm`
            fontWeight: 400,
            letterSpacing: '0',
            lineHeight: '1.125rem',
          },
          arrow: {
            color: elevateColorPalette.surface.brandDark,
          },
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            height: '36px',
          },
        },
      },
    },
  },
});

// Add MUI utility functions to the theme (required for MUI v7 components like Alert)
export const theme = Object.assign(baseTheme, {
  lighten,
  darken,
  alpha,
});

// TODO ban-ui-lib: delete the following once ban-ui-lib provides it
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headingXxl: true;
    headingXl: true;
    headingLg: true;
    headingMd: true;
    headingSm: true;
    headingXs: true;
    headingXxs: true;
    paragraphXl: true;
    paragraphLg: true;
    paragraph: true;
    paragraphSm: true;
    labelLg: true;
    labelLgLight: true;
    labelLgMultiline: true;
    labelLgMultilineLight: true;
    label: true;
    labelLight: true;
    labelMultiline: true;
    labelMultilineLight: true;
    labelSm: true;
    labelSmLight: true;
    h1?: false;
    h2?: false;
    h3?: false;
    h4?: false;
    h5?: false;
    h6?: false;
    subtitle1?: false;
    subtitle2?: false;
    body1?: false;
    body2?: false;
    button?: false;
    caption?: false;
    overline?: false;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    menu: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    tag: true;
    selection: true;
  }
}
